import React, { FC, Fragment, useEffect, useState } from "react";
import { Sidebar, AdminNavbar } from "@onpreo/components";
import { userRoutes, routes } from "./routes";
import { useReduxSelector } from "../../store/selectors";
import { getUser, getUserId } from "../../store/selectors/user.selectors";
import { Autocomplete } from "./autocomplete";
import { makeStyles, createStyles, ButtonBase, Divider, Tooltip, Chip } from "@material-ui/core";
import { getSalutation, getWorkspaceContents, getPipelineCenter, getWorkspaceBillingPrice, getWorkspace } from "../../store/selectors/workspace.selectors";
import { useDispatch, useSelector } from "react-redux";
import { AnnouncementOutlined, ExploreOutlined, HelpOutline } from "@material-ui/icons";
import { $updateUser } from "../../store/thunks/user.thunk";
import ExploreIcon from "@material-ui/icons/Explore";
import { userPilotTrigger } from "../helpers/userpilot-helper";
import { getPipelines, getAccessedPipelines } from "src/store/selectors/pipeline.selector";
import { generateRoutesList } from "./routes-generation-helper";
import { getFullAccessedPipelines } from "../pipeline-center/pipeline-center-helpers";
import SubscriptionStateBanner from "../helpers/subscription-state-banner";
import checkSubscription from "../../utils/subscription-limits/check-subscription";
import { priceToLicense, StripePlanTier } from "../../api/util/stripe/stripe-secrets";

export const SIDEBAR_OPEN_WIDTH = 250;
export const APP_BAR_HEIGHT = 76;

const useStyles = makeStyles(theme =>
    createStyles({
        container: {
            [theme.breakpoints.up("xl")]: {
                marginLeft: SIDEBAR_OPEN_WIDTH
            }
        },
        bannerHighlight: {
            backgroundImage: "linear-gradient(90deg, #fff, #ff5e69 60%, #ffa84b)",
            webkitTextFillColor: "transparent",
            textFillColor: "transparent",
            opacity: 1,
            webkitBackgroundClip: "text",
            backgroundClip: "text",
            fontStyle: "italic",
            display: "inline-block"
        }
    })
);

const Navigation: FC = props => {
    const user = useReduxSelector(getUser);
    const salutation = useSelector(getSalutation);
    const defaultImageSrc = salutation === "Frau" ? "/Business_Woman.png" : "/Business_man.png";
    const dispatch = useDispatch();
    const userId = useReduxSelector(getUserId);
    const workspace = useReduxSelector(getWorkspace);
    const price = useReduxSelector(getWorkspaceBillingPrice);
    const currentPipelines = useReduxSelector(getPipelines);
    const pipelineCenter = useReduxSelector(getPipelineCenter);
    const accessed = useReduxSelector(getAccessedPipelines);
    const [currentRoutes, setCurrentRoutes] = useState([]);
    const classes = useStyles();
    const contents = useReduxSelector(getWorkspaceContents);
    const displayContent = {};
    const contentTypes = ["guides", "landingPages", "checklists"];

    useEffect(() => {
        if (!price) return;
        const accessSalesPipelineSub = checkSubscription(price, "accessSalesPipeline");
        const getValue = (w, path) => {
            return path.split(".").reduce((acc, part) => acc && acc[part], w);
        };

        if (priceToLicense[price] !== StripePlanTier.PF && accessed) {
            (async () => {
                const fullAccessed = getFullAccessedPipelines(accessed, currentPipelines);
                const newRoutes = await generateRoutesList({ customPipelines: fullAccessed, pipelineCenter, accessSalesPipelineSub, workspace });
                const navRoutes = newRoutes
                    .filter(r => (!!r.checkWorkspaceAccess ? getValue(workspace, r.checkWorkspaceAccess) : true))
                    .filter(r => (!!r.restrictedFeature ? checkSubscription(price, r.restrictedFeature as any) : true));
                setCurrentRoutes(navRoutes);
            })();
        } else {
            const navRoutes = routes(workspace)
                .filter(r => (!!r.checkWorkspaceAccess ? getValue(workspace, r.checkWorkspaceAccess) : true))
                .filter(r => (!!r.restrictedFeature ? checkSubscription(price, r.restrictedFeature as any) : true));
            setCurrentRoutes(navRoutes);
        }
    }, [accessed, currentPipelines, pipelineCenter, price, workspace]);

    if (contents) {
        Object.keys(contents).forEach(content => {
            if (contents[content]) {
                if (contentTypes.includes(content)) {
                    Object.keys(contents[content]).forEach(item => {
                        if (!!contents[content][item] && (contents[content][item] === true || contents[content][item]["booked"])) {
                            displayContent[content] = true;
                        }
                    });
                }
            }
        });
    }

    const handleClick = () => {
        const data = { isFirstLogin: false };
        dispatch($updateUser({ userId: userId, user: { ...data } }));
    };

    // useEffect(() => {
    //     if (!billing.subscriptionInfo) (async () => await dispatch($fetchSubscription()))();
    //     else {
    //         const today = new Date();
    //         const trialEnd = new Date(billing?.subscriptionInfo?.["trial_end"]);
    //         const diffDays = dateDiffInDays(today, trialEnd);
    //         setTrialDays(diffDays);
    //     }
    // }, [billing]);

    const plan = price ? (priceToLicense[price] === StripePlanTier.PF ? "Preis-Finder" : priceToLicense[price]?.split("_")[0]) : "";
    const onOfficeImport = workspace?.billing?.marketplace?.onOffice?.fromMarketplace;

    return (
        <Fragment>
            <AdminNavbar
                userRoutes={userRoutes}
                user={{ name: user?.meta?.name, imgSrc: user?.meta?.img?.src ?? defaultImageSrc, imgAlt: "..." }}
                demo={user?.isFirstLogin}
                show={Object.keys(displayContent).length > 0}
                // trialDays={trialDays < 0 ? 0 : trialDays}
                // isTrialing={billing.subscriptionState === "trialing"}
            >
                {user?.mailing?.reauthenticate && (
                    <Chip style={{ background: "red", marginRight: 16, height: 30, color: "#fff" }} label="Mailverbindung erneuern" />
                )}
                <Tooltip title={"Die kostenlose Akquise-Academy für Makler."}>
                    <ButtonBase href="/academy" disableRipple style={{ marginRight: 0, fontWeight: 500 }}>
                        <span className={classes.bannerHighlight}>Akquise-Academy</span>
                    </ButtonBase>
                </Tooltip>
                <Divider orientation="vertical" style={{ margin: "0 16px", backgroundColor: "white", color: "white", height: "20px" }} />
                <Tooltip title={"Starten Sie eine Tour, um unsere Software besser kennen zu lernen."}>
                    <ButtonBase
                        onClick={event => {
                            event.preventDefault();
                            userPilotTrigger.tourStart();
                        }}
                        style={{ marginRight: 16 }}
                    >
                        <ExploreOutlined style={{ width: 20, height: 20, marginRight: 4 }} />
                    </ButtonBase>
                </Tooltip>
                <Tooltip title={"Hier präsentieren wir Ihnen unsere neusten Features und Updates"}>
                    <ButtonBase href="https://www.onpreo.com/updates" target="_blank" rel="noopener noreferrer" disableRipple style={{ marginRight: 16 }}>
                        <AnnouncementOutlined style={{ width: 20, height: 20, marginRight: 4 }} />
                    </ButtonBase>
                </Tooltip>
                <Tooltip title={"Sie benötigen Hilfe? In unserem Help Center finden Sie Artikel rund um unsere Software."}>
                    <ButtonBase href="https://intercom.help/onpreo/de/" target="_blank" rel="noopener noreferrer" disableRipple>
                        <HelpOutline style={{ width: 20, height: 20, marginRight: 4 }} />
                    </ButtonBase>
                </Tooltip>
                <Divider orientation="vertical" style={{ margin: "0 16px", backgroundColor: "white", color: "white", height: "20px" }} />
                <Autocomplete />
            </AdminNavbar>
            <SubscriptionStateBanner />
            <Sidebar
                closeSidebarResponsive={() => console.log("hi there")}
                openResponsive={true}
                routes={currentRoutes}
                logo={{ imgSrc: "/logo.png", imgAlt: "onpreo", innerLink: "/profile" }}
                user={{ name: user?.meta?.name, imgSrc: user?.meta?.img?.src ?? defaultImageSrc, imgAlt: "..." }}
                showNotificationText={user?.isFirstLogin}
                onClick={handleClick}
                plan={plan}
                extraChip={onOfficeImport ? "OnOffice Import" : null}
            />
            <div id={"app-content"} className={classes.container}>
                {props.children}
            </div>
        </Fragment>
    );
};

export default Navigation;
